import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Info,
  ChevronDown,
  ChevronUp,
  User,
  Package,
  Monitor,
  GitBranch,
  RotateCcw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Status/type configs (mirrors UATInbox) ────────────────────────────────────

const STATUS_CFG = {
  SUBMITTED:        { label: "Submitted",        color: "text-sky-600 dark:text-sky-400",       bg: "bg-sky-50 dark:bg-sky-950/30",       border: "border-sky-200 dark:border-sky-800" },
  TRIAGED:          { label: "Triaged",           color: "text-violet-600 dark:text-violet-400",  bg: "bg-violet-50 dark:bg-violet-950/30",  border: "border-violet-200 dark:border-violet-800" },
  ACCEPTED:         { label: "Accepted",          color: "text-blue-600 dark:text-blue-400",     bg: "bg-blue-50 dark:bg-blue-950/30",     border: "border-blue-200 dark:border-blue-800" },
  IN_PROGRESS:      { label: "In Progress",       color: "text-amber-600 dark:text-amber-400",   bg: "bg-amber-50 dark:bg-amber-950/30",   border: "border-amber-200 dark:border-amber-800" },
  READY_FOR_RETEST: { label: "Ready for Retest",  color: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-50 dark:bg-indigo-950/30", border: "border-indigo-200 dark:border-indigo-800" },
  RETEST_FAILED:    { label: "Retest Failed",     color: "text-red-600 dark:text-red-400",       bg: "bg-red-50 dark:bg-red-950/30",       border: "border-red-200 dark:border-red-800" },
  RETEST_PASSED:    { label: "Retest Passed",     color: "text-emerald-600 dark:text-emerald-400",bg: "bg-emerald-50 dark:bg-emerald-950/30",border: "border-emerald-200 dark:border-emerald-800" },
  CLOSED:           { label: "Closed",            color: "text-muted-foreground",                bg: "bg-muted/50",                        border: "border-border" },
  REJECTED:         { label: "Rejected",          color: "text-red-500 dark:text-red-400",       bg: "bg-red-50 dark:bg-red-950/30",       border: "border-red-200 dark:border-red-800" },
  DUPLICATE:        { label: "Duplicate",         color: "text-orange-500 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-950/30", border: "border-orange-200 dark:border-orange-800" },
  DEFERRED:         { label: "Deferred",          color: "text-slate-500 dark:text-slate-400",   bg: "bg-slate-50 dark:bg-slate-950/30",   border: "border-slate-200 dark:border-slate-800" },
};

const SEVERITY_CFG = {
  CRITICAL:     { color: "text-red-600 dark:text-red-400",      dot: "bg-red-500" },
  HIGH:         { color: "text-orange-500 dark:text-orange-400", dot: "bg-orange-400" },
  MEDIUM:       { color: "text-amber-600 dark:text-amber-400",   dot: "bg-amber-400" },
  LOW:          { color: "text-slate-500 dark:text-slate-400",   dot: "bg-slate-400" },
  INFORMATIONAL:{ color: "text-blue-500 dark:text-blue-400",     dot: "bg-blue-400" },
};

// Frozen transition graph — mirrors backend for UI visibility
const LIFECYCLE_ACTIONS = {
  SUBMITTED:        [
    { to: "TRIAGED",   label: "Triage",       requiresNote: false, isDanger: false },
    { to: "REJECTED",  label: "Reject",        requiresNote: true,  isDanger: true },
  ],
  TRIAGED:          [
    { to: "ACCEPTED",  label: "Accept",        requiresNote: false, isDanger: false },
    { to: "DEFERRED",  label: "Defer",         requiresNote: true,  isDanger: false },
    { to: "DUPLICATE", label: "Mark Duplicate",requiresNote: true,  isDanger: false, requiresDupId: true },
    { to: "REJECTED",  label: "Reject",        requiresNote: true,  isDanger: true },
  ],
  ACCEPTED:         [
    { to: "IN_PROGRESS", label: "Start Work",  requiresNote: false, isDanger: false },
    { to: "DEFERRED",    label: "Defer",       requiresNote: true,  isDanger: false },
    { to: "REJECTED",    label: "Reject",      requiresNote: true,  isDanger: true },
  ],
  IN_PROGRESS:      [
    { to: "READY_FOR_RETEST", label: "Send for Retest", requiresNote: false, isDanger: false },
    { to: "CLOSED",           label: "Close (non-BUG)", requiresNote: false, isDanger: false, nonBugOnly: true },
  ],
  READY_FOR_RETEST: [
    { to: "RETEST_PASSED", label: "Retest Passed", requiresNote: false, isDanger: false },
    { to: "RETEST_FAILED", label: "Retest Failed", requiresNote: false, isDanger: true },
  ],
  RETEST_FAILED:    [
    { to: "IN_PROGRESS", label: "Reopen Work", requiresNote: false, isDanger: false },
  ],
  RETEST_PASSED:    [
    { to: "CLOSED", label: "Close", requiresNote: false, isDanger: false },
  ],
  DEFERRED:         [
    { to: "TRIAGED",   label: "Reopen to Triage", requiresNote: false, isDanger: false },
    { to: "ACCEPTED",  label: "Accept",            requiresNote: false, isDanger: false },
  ],
  REJECTED:         [{ to: "TRIAGED", label: "Force Reopen (Super Admin)", requiresNote: true, isDanger: false, isForced: true }],
  DUPLICATE:        [{ to: "TRIAGED", label: "Force Reopen (Super Admin)", requiresNote: true, isDanger: false, isForced: true }],
  CLOSED:           [{ to: "TRIAGED", label: "Force Reopen (Super Admin)", requiresNote: true, isDanger: false, isForced: true }],
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function ContentSection({ title, children }) {
  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{title}</h3>
      <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
        {children || <span className="text-muted-foreground/50 italic">Not provided</span>}
      </div>
    </div>
  );
}

function HistoryEntry({ entry, idx }) {
  const isForced = entry.event_marker === "FORCED_REOPEN";
  const isAssigned = entry.event_marker === "ASSIGNED";
  const toStatus = STATUS_CFG[entry.to_status];

  return (
    <div className={`flex items-start gap-3 py-3 border-b border-border last:border-0 ${isForced ? "bg-orange-50/50 dark:bg-orange-950/10" : ""}`} data-testid={`history-entry-${idx}`}>
      <div className="shrink-0 mt-0.5">
        {entry.to_status === "CLOSED" || entry.to_status === "RETEST_PASSED" ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        ) : entry.to_status === "REJECTED" || entry.to_status === "RETEST_FAILED" ? (
          <XCircle className="h-4 w-4 text-red-500" />
        ) : (
          <Clock className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {entry.from_status && (
            <span className="text-xs text-muted-foreground">{entry.from_status?.replace(/_/g, " ")}</span>
          )}
          {entry.from_status && <span className="text-xs text-muted-foreground">→</span>}
          <span className={`text-xs font-semibold ${toStatus?.color || "text-foreground"}`}>
            {entry.to_status?.replace(/_/g, " ")}
          </span>
          {isForced && (
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
              Forced Reopen
            </span>
          )}
          {isAssigned && (
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
              Assigned
            </span>
          )}
        </div>
        {entry.note && <p className="text-xs text-muted-foreground mt-0.5 italic">"{entry.note}"</p>}
      </div>
      <span className="text-[10px] text-muted-foreground/60 shrink-0">
        {new Date(entry.created_at).toLocaleString()}
      </span>
    </div>
  );
}

// ── Action Panel ──────────────────────────────────────────────────────────────

function ActionPanel({ feedback, onTransition, loading: acting, userRole }) {
  const [showNoteFor, setShowNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const [dupId, setDupId] = useState("");
  const [error, setError] = useState(null);

  const allActions = LIFECYCLE_ACTIONS[feedback.status] || [];
  // Hide FORCED_REOPEN actions from non-super_admin users (backend also guards, but UX is cleaner)
  const actions = allActions.filter((a) => !a.isForced || userRole === "super_admin");

  const handleAction = async (action) => {
    if (action.requiresNote || action.requiresDupId) {
      setShowNoteFor(action.to);
      setNote("");
      setDupId("");
      setError(null);
      return;
    }
    setError(null);
    await onTransition(action.to, "", "", false);
  };

  const handleConfirm = async () => {
    const action = actions.find((a) => a.to === showNoteFor);
    if (!action) return;
    if (action.requiresNote && !note.trim()) {
      setError("A note is required for this action.");
      return;
    }
    if (action.requiresDupId && !dupId.trim()) {
      setError("The ID of the original (canonical) feedback record is required.");
      return;
    }
    const err = await onTransition(showNoteFor, note.trim(), dupId.trim(), action.isForced);
    if (err) {
      setError(err);
    } else {
      setShowNoteFor(null);
      setNote("");
      setDupId("");
    }
  };

  if (actions.length === 0) return null;

  return (
    <div data-testid="feedback-action-panel" className="rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground mb-3">Actions</h2>

      {/* Action buttons */}
      {showNoteFor === null && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => {
            const isBugBlock = action.nonBugOnly && feedback.feedback_type === "BUG";
            return (
              <button
                key={action.to}
                data-testid={`action-btn-${action.to}`}
                onClick={() => handleAction(action)}
                disabled={acting || isBugBlock}
                title={isBugBlock ? "BUG items must go through retest before closing" : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-opacity disabled:opacity-40 ${
                  action.isForced
                    ? "border-orange-300 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/20"
                    : action.isDanger
                    ? "border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
                    : "bg-primary text-primary-foreground border-transparent hover:opacity-90"
                }`}
              >
                {action.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Note/confirm form */}
      {showNoteFor !== null && (
        <div className="space-y-3" data-testid="action-confirm-form">
          <p className="text-xs text-muted-foreground">
            Moving to <strong>{showNoteFor.replace(/_/g, " ")}</strong>
            {actions.find((a) => a.to === showNoteFor)?.requiresNote && " — note required"}
          </p>

          {actions.find((a) => a.to === showNoteFor)?.requiresDupId && (
            <input
              type="text"
              placeholder="Canonical feedback ID (UUID)…"
              value={dupId}
              onChange={(e) => setDupId(e.target.value)}
              className="w-full h-8 px-3 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
              data-testid="action-dup-id-input"
            />
          )}

          <textarea
            placeholder="Add a note…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
            data-testid="action-note-input"
          />

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400" data-testid="action-error">{error}</p>
          )}

          <div className="flex gap-2">
            <button
              data-testid="action-confirm-btn"
              onClick={handleConfirm}
              disabled={acting}
              className="flex-1 h-8 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {acting ? "Processing…" : "Confirm"}
            </button>
            <button
              data-testid="action-cancel-btn"
              onClick={() => { setShowNoteFor(null); setError(null); }}
              className="px-3 h-8 text-xs rounded-md border border-border text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function UATFeedbackDetail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { feedbackId } = useParams();

  const [feedback, setFeedback] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState(null);

  const userRole = user?.role;

  const fetchFeedback = useCallback(async () => {
    const token = getToken();
    if (!token || !feedbackId) return;
    setLoading(true);
    setError(null);
    try {
      const [fbRes, histRes] = await Promise.all([
        fetch(`${API}/api/uat/feedback/${feedbackId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/uat/feedback/${feedbackId}/history`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!fbRes.ok) throw new Error(`HTTP ${fbRes.status}`);
      const fbData = await fbRes.json();
      setFeedback(fbData);
      if (histRes.ok) {
        const histData = await histRes.json();
        setHistory(histData.history || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [feedbackId]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const handleTransition = async (newStatus, note, dupId) => {
    const token = getToken();
    if (!token) return "Not authenticated";
    setActing(true);
    try {
      const body = { status: newStatus };
      if (note) body.note = note;
      if (dupId) body.duplicate_of_feedback_id = dupId;

      const res = await fetch(`${API}/api/uat/feedback/${feedbackId}/status`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        return errData.detail?.message || errData.detail || `HTTP ${res.status}`;
      }
      await fetchFeedback();
      return null; // success
    } catch (e) {
      return e.message;
    } finally {
      setActing(false);
    }
  };

  const sfg = feedback ? (STATUS_CFG[feedback.status] || STATUS_CFG.SUBMITTED) : null;
  const sevcfg = feedback ? (SEVERITY_CFG[feedback.severity] || SEVERITY_CFG.MEDIUM) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="uat-detail-page">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="uat-detail-back-btn"
                onClick={() => navigate("/setup/uat-inbox")}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                UAT Inbox
              </button>
              {feedback && (
                <>
                  <span className="text-muted-foreground/30">/</span>
                  <span className="text-xs text-foreground font-medium truncate max-w-48">{feedback.title}</span>
                </>
              )}
            </div>
            <button
              data-testid="uat-detail-refresh-btn"
              onClick={fetchFeedback}
              disabled={loading || acting}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-md border border-border"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        {loading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-24 rounded-xl border border-border bg-muted/30 animate-pulse" />
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30" data-testid="uat-detail-error">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <button onClick={fetchFeedback} className="ml-auto text-xs text-red-600 underline">Retry</button>
          </div>
        )}

        {!loading && !error && feedback && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-5">
              {/* Title + badges */}
              <div data-testid="uat-detail-header">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${sfg?.bg} ${sfg?.color} ${sfg?.border}`}
                    data-testid="feedback-status-badge">
                    {sfg?.label || feedback.status}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${sevcfg?.color}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${sevcfg?.dot}`} />
                    {feedback.severity}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{feedback.feedback_type}</span>
                </div>
                <h1 className="text-xl font-bold text-foreground">{feedback.title}</h1>
                <p className="text-xs text-muted-foreground mt-1">
                  #{feedback.id.slice(-6).toUpperCase()} · Submitted {new Date(feedback.created_at).toLocaleString()}
                </p>
              </div>

              {/* Content sections */}
              {feedback.description && <ContentSection title="Description">{feedback.description}</ContentSection>}
              {feedback.steps_to_reproduce && <ContentSection title="Steps to Reproduce">{feedback.steps_to_reproduce}</ContentSection>}
              {feedback.expected_behavior && <ContentSection title="Expected Behavior">{feedback.expected_behavior}</ContentSection>}
              {feedback.actual_behavior && <ContentSection title="Actual Behavior">{feedback.actual_behavior}</ContentSection>}
              {feedback.normalized_english_text && feedback.original_language && feedback.original_language !== "en" && (
                <ContentSection title={`Original (${feedback.original_language})`}>{feedback.original_text}</ContentSection>
              )}

              {/* Evaluation history */}
              {history.length > 0 && (
                <div data-testid="feedback-history-section">
                  <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    Status History
                    <span className="text-xs font-normal text-muted-foreground">append-only audit trail</span>
                  </h2>
                  <div className="rounded-xl border border-border bg-card p-4">
                    {history.map((h, i) => <HistoryEntry key={h.id} entry={h} idx={i} />)}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar column */}
            <div className="space-y-4">
              {/* Action panel */}
              <ActionPanel
                feedback={feedback}
                onTransition={handleTransition}
                loading={acting}
                userRole={userRole}
              />

              {/* Context */}
              <div className="rounded-xl border border-border bg-card p-4" data-testid="feedback-context-section">
                <h2 className="text-sm font-semibold text-foreground mb-3">Context</h2>
                <dl className="space-y-2">
                  {[
                    { icon: Package, label: "Build", value: feedback.build_version },
                    { icon: Monitor, label: "Environment", value: feedback.environment },
                    { icon: GitBranch, label: "Stage", value: feedback.setup_stage },
                    { icon: Info, label: "Module", value: feedback.module },
                    { icon: Info, label: "Screen", value: feedback.screen },
                    { icon: User, label: "Assigned To", value: feedback.assigned_to ? feedback.assigned_to.slice(-8) : null },
                  ].filter(f => f.value).map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center justify-between gap-2">
                      <dt className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                        <Icon className="h-3.5 w-3.5" />{label}
                      </dt>
                      <dd className="text-xs font-mono text-foreground truncate text-right">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              {/* Resolution */}
              {feedback.resolution && (
                <div className="rounded-xl border border-border bg-card p-4" data-testid="feedback-resolution-section">
                  <h2 className="text-sm font-semibold text-foreground mb-2">Resolution</h2>
                  <p className="text-xs text-foreground leading-relaxed">{feedback.resolution}</p>
                  {feedback.resolution_version && (
                    <p className="text-xs text-muted-foreground mt-1">Version: {feedback.resolution_version}</p>
                  )}
                </div>
              )}

              {/* Duplicate link */}
              {feedback.duplicate_of_feedback_id && (
                <div className="rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/20 p-3" data-testid="duplicate-of-section">
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Duplicate of</p>
                  <button
                    onClick={() => navigate(`/setup/uat-inbox/${feedback.duplicate_of_feedback_id}`)}
                    className="text-xs font-mono text-orange-700 dark:text-orange-300 underline mt-0.5 block"
                    data-testid="duplicate-of-link"
                  >
                    #{feedback.duplicate_of_feedback_id.slice(-6).toUpperCase()}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
